import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatDetail } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Loader2, RefreshCw, ShieldAlert, TrendingUp, X } from "lucide-react";

const FLAG_LABEL = {
  accredited_over_registered: "Accredited > registered",
  votes_over_accredited:      "Votes > accredited",
  mismatch_high:              "High mismatch (>5%)",
  candidate_zero_votes:       "Candidate got zero",
};
const FLAG_COLOR = {
  accredited_over_registered: "#FF453A",
  votes_over_accredited:      "#FF453A",
  mismatch_high:              "#FF9F0A",
  candidate_zero_votes:       "#5AC8FA",
};

function riskTier(score) {
  if (score >= 6) return { label: "Critical", color: "#AF52DE" };
  if (score >= 4) return { label: "High",     color: "#FF453A" };
  if (score >= 2) return { label: "Medium",   color: "#FF9F0A" };
  return                { label: "Low",       color: "#5AC8FA" };
}

export default function FraudDashboard() {
  const [elections, setElections] = useState([]);
  const [electionId, setElectionId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [flagFilter, setFlagFilter] = useState("");
  const [tierFilter, setTierFilter] = useState("");

  useEffect(() => {
    api.get("/elections").then(({ data }) => {
      setElections(data);
      const active = data.find(e => e.status === "active") || data[0];
      if (active) setElectionId(active.id);
    }).catch(() => {});
  }, []);

  const load = async () => {
    if (!electionId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/fraud/summary`, { params: { election_id: electionId }});
      setData(data);
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [electionId]);

  const hotspots = data?.hotspots || [];
  const filtered = useMemo(() => hotspots.filter(h => {
    if (flagFilter && !h.flags.includes(flagFilter)) return false;
    if (tierFilter && riskTier(h.risk_score).label.toLowerCase() !== tierFilter) return false;
    return true;
  }), [hotspots, flagFilter, tierFilter]);

  const maxRegionRisk = Math.max(1, ...(data?.by_region || []).map(r => r.risk_score));
  const flaggedPct = data?.total_results ? Math.round((data.total_flagged / data.total_results) * 100) : 0;

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1600px]">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Fraud & Anomaly</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2" data-testid="fraud-title">Risk Board</h1>
          <p className="text-zinc-400 text-sm mt-2 max-w-xl">Every polling-unit submission that tripped a heuristic — ranked by risk so you triage the shakiest units first.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={electionId} onChange={e=>setElectionId(e.target.value)} data-testid="fraud-election-select"
            className="bg-transparent border border-white/20 px-3 py-2 text-xs outline-none">
            {elections.map(e => <option key={e.id} value={e.id} className="bg-black">{e.name}</option>)}
          </select>
          <button onClick={load} data-testid="fraud-refresh" className="p-2 border border-white/20 hover:bg-white/10">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""}/>
          </button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="border border-white/10 p-5" data-testid="kpi-flagged">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Flagged units</div>
          <div className="font-display text-4xl tabular mt-2 text-red-400">{data?.total_flagged ?? 0}</div>
          <div className="text-xs text-zinc-500 mt-1">of {data?.total_results ?? 0} · {flaggedPct}%</div>
        </div>
        <div className="border border-white/10 p-5">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Critical risk</div>
          <div className="font-display text-4xl tabular mt-2" style={{ color: "#AF52DE" }}>{hotspots.filter(h=>h.risk_score>=6).length}</div>
          <div className="text-xs text-zinc-500 mt-1">score ≥ 6</div>
        </div>
        <div className="border border-white/10 p-5">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">High risk</div>
          <div className="font-display text-4xl tabular mt-2 text-red-500">{hotspots.filter(h=>h.risk_score>=4 && h.risk_score<6).length}</div>
          <div className="text-xs text-zinc-500 mt-1">score 4-5</div>
        </div>
        <div className="border border-white/10 p-5">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Watchlist</div>
          <div className="font-display text-4xl tabular mt-2 text-amber-400">{hotspots.filter(h=>h.risk_score>=2 && h.risk_score<4).length}</div>
          <div className="text-xs text-zinc-500 mt-1">score 2-3</div>
        </div>
      </div>

      {/* Regional heatmap */}
      <div className="mt-10 border border-white/10">
        <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-2"><TrendingUp size={13}/> Regional heatmap</div>
          <div className="text-xs text-zinc-500">{data?.by_region?.length || 0} regions · sorted by risk</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 grid-tech" data-testid="fraud-heatmap">
          {(data?.by_region || []).map(r => {
            const intensity = Math.min(1, r.risk_score / maxRegionRisk);
            const bg = `rgba(255, 69, 58, ${(intensity * 0.65).toFixed(2)})`;
            return (
              <div key={r.region_id} className="p-5 relative overflow-hidden transition-colors hover:bg-white/[0.03]" data-testid={`heat-region-${r.region_id}`}>
                <div className="absolute inset-0 pointer-events-none" style={{ background: bg }} />
                <div className="relative">
                  <div className="text-xs uppercase tracking-[0.2em] text-zinc-300">{r.region_name}</div>
                  <div className="font-display text-4xl tabular mt-2 text-white">{r.risk_score}</div>
                  <div className="text-xs text-zinc-400 mt-1">
                    <span className="text-red-300 font-medium">{r.flagged_count}</span> flagged of {r.total_count} units
                  </div>
                </div>
              </div>
            );
          })}
          {!data?.by_region?.length && <div className="p-8 text-zinc-500 col-span-full text-sm">No regional data yet.</div>}
        </div>
      </div>

      {/* Filters */}
      <div className="mt-8 flex flex-wrap items-center gap-2 text-xs">
        <span className="uppercase tracking-[0.2em] text-zinc-500 mr-2">Filter</span>
        <select value={flagFilter} onChange={e=>setFlagFilter(e.target.value)} data-testid="filter-flag"
          className="bg-transparent border border-white/20 px-3 py-1.5 outline-none">
          <option value="" className="bg-black">Any flag</option>
          {Object.entries(FLAG_LABEL).map(([k,v]) => <option key={k} value={k} className="bg-black">{v}</option>)}
        </select>
        <select value={tierFilter} onChange={e=>setTierFilter(e.target.value)} data-testid="filter-tier"
          className="bg-transparent border border-white/20 px-3 py-1.5 outline-none">
          <option value="" className="bg-black">Any risk tier</option>
          <option value="critical" className="bg-black">Critical</option>
          <option value="high" className="bg-black">High</option>
          <option value="medium" className="bg-black">Medium</option>
          <option value="low" className="bg-black">Low</option>
        </select>
        {(flagFilter || tierFilter) && <button onClick={()=>{setFlagFilter("");setTierFilter("");}} className="underline text-zinc-400">clear</button>}
      </div>

      {/* Hotspots */}
      <div className="mt-4 border border-white/10 data-table">
        <div className="grid grid-cols-12 px-4 py-3 border-b border-white/10 text-[10px] uppercase tracking-[0.3em] text-zinc-500">
          <div className="col-span-1">Risk</div>
          <div className="col-span-3">Polling unit</div>
          <div className="col-span-2">Ward · Region</div>
          <div className="col-span-4">Flags</div>
          <div className="col-span-2 text-right">Mismatch · Status</div>
        </div>
        <div className="divide-y divide-white/5" data-testid="hotspots-list">
          {loading && <div className="p-6 text-zinc-500 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Scanning submissions…</div>}
          {!loading && filtered.map(h => {
            const tier = riskTier(h.risk_score);
            return (
              <button key={h.result_id} onClick={()=>setSelected(h)} data-testid={`hotspot-${h.result_id}`}
                className="w-full text-left grid grid-cols-12 px-4 py-3 items-center hover:bg-white/[0.03]">
                <div className="col-span-1">
                  <div className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-semibold text-black tabular" style={{ background: tier.color }}>
                    {h.risk_score}
                  </div>
                </div>
                <div className="col-span-3 min-w-0">
                  <div className="text-sm truncate">{h.polling_unit_name}</div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">{h.polling_unit_code} · v{h.version}</div>
                </div>
                <div className="col-span-2 text-xs text-zinc-400 truncate">
                  <div>{h.ward_name}</div>
                  <div className="text-zinc-500">{h.region_name}</div>
                </div>
                <div className="col-span-4 flex flex-wrap gap-1">
                  {h.flags.map(f => (
                    <span key={f} className="text-[10px] px-1.5 py-0.5 uppercase tracking-wider"
                      style={{ background: (FLAG_COLOR[f]||"#71717A")+"22", color: FLAG_COLOR[f] || "#a1a1aa", border: `1px solid ${(FLAG_COLOR[f]||"#71717A")}55` }}>
                      {FLAG_LABEL[f] || f}
                    </span>
                  ))}
                </div>
                <div className="col-span-2 text-right text-xs">
                  <div className="tabular">±{h.mismatch.toLocaleString()}</div>
                  <div className={`uppercase tracking-wider text-[10px] mt-0.5 ${h.status==="approved" ? "text-emerald-300" : h.status==="rejected" ? "text-red-300" : "text-amber-300"}`}>{h.status}</div>
                </div>
              </button>
            );
          })}
          {!loading && !filtered.length && (
            <div className="p-8 text-zinc-500 text-sm text-center flex flex-col items-center gap-2">
              <ShieldAlert className="text-emerald-400" size={22}/>
              No flagged submissions match. The board is calm.
            </div>
          )}
        </div>
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={()=>setSelected(null)} data-testid="hotspot-detail">
          <div onClick={e=>e.stopPropagation()} className="relative bg-[#0F0F12] border border-red-500/40 max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <button onClick={()=>setSelected(null)} className="absolute top-4 right-4 p-2 text-zinc-500 hover:text-white" data-testid="close-hotspot"><X size={16}/></button>
            <div className="text-xs uppercase tracking-[0.3em] text-red-400 flex items-center gap-2"><AlertTriangle size={13}/> Risk score {selected.risk_score} · {riskTier(selected.risk_score).label}</div>
            <h2 className="font-display text-3xl mt-2">{selected.polling_unit_name}</h2>
            <div className="text-xs text-zinc-500 mt-1">{selected.polling_unit_code} · {selected.ward_name} · {selected.region_name} · v{selected.version}</div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
              <div className="border border-white/10 p-3"><div className="text-[10px] uppercase tracking-wider text-zinc-500">Registered</div><div className="font-display text-2xl tabular mt-1">{selected.total_registered.toLocaleString()}</div></div>
              <div className="border border-white/10 p-3"><div className="text-[10px] uppercase tracking-wider text-zinc-500">Accredited</div><div className="font-display text-2xl tabular mt-1">{selected.total_accredited.toLocaleString()}</div></div>
              <div className="border border-white/10 p-3"><div className="text-[10px] uppercase tracking-wider text-zinc-500">Vote sum</div><div className="font-display text-2xl tabular mt-1">{selected.vote_sum.toLocaleString()}</div></div>
              <div className="border border-white/10 p-3"><div className="text-[10px] uppercase tracking-wider text-zinc-500">Declared valid</div><div className="font-display text-2xl tabular mt-1">{selected.total_valid.toLocaleString()}</div></div>
            </div>

            <div className="mt-6">
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">Triggered flags</div>
              <div className="flex flex-wrap gap-2">
                {selected.flags.map(f => (
                  <span key={f} className="text-xs px-2 py-1 uppercase tracking-wider"
                    style={{ background: (FLAG_COLOR[f]||"#71717A")+"22", color: FLAG_COLOR[f] || "#a1a1aa", border: `1px solid ${(FLAG_COLOR[f]||"#71717A")}55` }}>
                    {FLAG_LABEL[f] || f}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-2 border-t border-white/10 pt-4">
              <Link to="/app/approvals" data-testid="go-approvals" className="px-3 py-2 border border-white/20 text-xs uppercase tracking-[0.2em] hover:bg-white/10">Open in approvals</Link>
              <div className="ml-auto text-xs text-zinc-500 self-center">
                Submitted by {(selected.submitted_by_email||"").split("@")[0]} · {(selected.submitted_at||"").replace("T"," ").slice(0,16)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
